// src/pages/Earnings/Earnings.tsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { alpacaService, EarningsItem, EarningsCalendar } from '../../services/AlpacaService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './Earnings.module.css';

/** Local storage key for the user's ad-hoc extra symbols. */
const EXTRA_SYMBOLS_KEY = 'earnings.extra_symbols.v1';

/** Parse a free-form symbols input ("AAPL, nvda; META") into a clean uppercase array. */
function parseSymbolsInput(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean),
    ),
  );
}

/** Format "bmo" / "amc" / "" into readable label */
function earningsHour(hour: string): string {
  if (hour === 'bmo') return 'Before Open';
  if (hour === 'amc') return 'After Close';
  return 'TBD';
}

/** Format revenue in billions / millions */
function fmtRevenue(val: number | null): string {
  if (val === null || val === undefined) return '--';
  if (val >= 1_000_000_000) return `$${(val / 1_000_000_000).toFixed(1)}B`;
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(0)}M`;
  return `$${val.toLocaleString()}`;
}

/** Format EPS */
function fmtEps(val: number | null): string {
  if (val === null || val === undefined) return '--';
  return `$${val.toFixed(2)}`;
}

/** Get a friendly date string */
function fmtDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** Days until a date */
function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T12:00:00');
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

/** Group earnings by week */
function groupByWeek(items: EarningsItem[]): { label: string; items: EarningsItem[] }[] {
  const groups: Map<string, EarningsItem[]> = new Map();

  for (const item of items) {
    const d = new Date(item.date + 'T12:00:00');
    // Get Monday of that week
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d);
    monday.setDate(diff);
    const weekKey = monday.toISOString().slice(0, 10);

    if (!groups.has(weekKey)) groups.set(weekKey, []);
    groups.get(weekKey)!.push(item);
  }

  return Array.from(groups.entries()).map(([weekStart, items]) => {
    const friday = new Date(weekStart + 'T12:00:00');
    friday.setDate(friday.getDate() + 4);
    const label = `${fmtDate(weekStart)} - ${fmtDate(friday.toISOString().slice(0, 10))}`;
    return { label, items };
  });
}

const Earnings: React.FC = () => {
  const [data, setData] = useState<EarningsCalendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<'list' | 'week'>('list');

  // Symbol-source state (positions + watchlists + ad-hoc).
  const [positionSymbols, setPositionSymbols] = useState<string[]>([]);
  const [watchlistSymbols, setWatchlistSymbols] = useState<string[]>([]);
  const [extraInput, setExtraInput] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(EXTRA_SYMBOLS_KEY) || '';
  });

  // Build the union of all symbol sources (sorted, deduped).
  const extraSymbols = useMemo(() => parseSymbolsInput(extraInput), [extraInput]);
  const combinedSymbols = useMemo(() => {
    const set = new Set<string>();
    positionSymbols.forEach((s) => set.add(s));
    watchlistSymbols.forEach((s) => set.add(s));
    extraSymbols.forEach((s) => set.add(s));
    return Array.from(set).sort();
  }, [positionSymbols, watchlistSymbols, extraSymbols]);

  // Persist ad-hoc input so it survives page reloads.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (extraInput) window.localStorage.setItem(EXTRA_SYMBOLS_KEY, extraInput);
    else window.localStorage.removeItem(EXTRA_SYMBOLS_KEY);
  }, [extraInput]);

  // Load positions + watchlists in parallel on mount. Tolerate either
  // source failing — earnings page should still work if e.g. the user
  // hasn't connected a broker yet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [posResp, wlResp] = await Promise.allSettled([alpacaService.getPositions(), alpacaService.getWatchlists()]);
      if (cancelled) return;
      if (posResp.status === 'fulfilled') {
        const positions = (posResp.value as { positions?: { symbol?: string }[] } | undefined)?.positions || [];
        const syms = positions.map((p) => (p.symbol || '').toUpperCase()).filter(Boolean);
        setPositionSymbols(Array.from(new Set(syms)).sort());
      }
      if (wlResp.status === 'fulfilled') {
        const syms = (wlResp.value?.watchlists || []).flatMap((w) => (w.symbols || []).map((s) => s.toUpperCase()));
        setWatchlistSymbols(Array.from(new Set(syms)).sort());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch earnings whenever the combined symbol set changes.
  const fetchEarnings = useCallback(async (symbols: string[]) => {
    setLoading(true);
    setError('');
    try {
      const result = await alpacaService.getEarningsCalendar(symbols);
      if ('error' in result) {
        setError((result as any).error);
      } else {
        setData(result);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEarnings(combinedSymbols);
  }, [fetchEarnings, combinedSymbols]);

  const upcoming = data?.upcoming ?? [];
  const pastRecent = data?.past_recent ?? [];

  // Find next earnings event
  const nextEvent = upcoming.length > 0 ? upcoming[0] : null;
  const nextDays = nextEvent ? daysUntil(nextEvent.date) : null;

  // Count how many this week
  const today = new Date();
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
  const thisWeekCount = upcoming.filter((e) => {
    const d = new Date(e.date + 'T12:00:00');
    return d <= endOfWeek;
  }).length;

  const weekGroups = groupByWeek(upcoming);

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Earnings Calendar</h1>

      {loading && <SignalLoader message="Loading earnings data..." />}
      {error && <p className={styles.error}>{error}</p>}

      {data && (
        <>
          {/* Summary cards */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Next Earnings</span>
              <span className={styles.statValue}>
                {nextEvent ? (
                  <>
                    {nextEvent.symbol}
                    <span className={styles.statSub}>
                      {nextDays === 0 ? 'Today' : nextDays === 1 ? 'Tomorrow' : `in ${nextDays} days`}
                    </span>
                  </>
                ) : (
                  'None scheduled'
                )}
              </span>
            </div>

            <div className={styles.statCard}>
              <span className={styles.statLabel}>This Week</span>
              <span className={styles.statValue}>
                {thisWeekCount}
                <span className={styles.statSub}>reports</span>
              </span>
            </div>

            <div className={styles.statCard}>
              <span className={styles.statLabel}>Upcoming (90d)</span>
              <span className={styles.statValue}>
                {upcoming.length}
                <span className={styles.statSub}>
                  {data.filter_applied
                    ? `of ${combinedSymbols.length} tracked symbols`
                    : 'across all symbols (capped at 200)'}
                </span>
              </span>
            </div>
          </div>

          {/* ── Tracked symbols panel ── */}
          <div className={styles.sourceBox}>
            <div className={styles.sourceHeader}>
              <strong>Tracking earnings for</strong>{' '}
              <span className={styles.sourceCount}>
                {combinedSymbols.length} symbol{combinedSymbols.length === 1 ? '' : 's'}
              </span>
              <span className={styles.sourceBreakdown}>
                {positionSymbols.length} from positions · {watchlistSymbols.length} from watchlists ·{' '}
                {extraSymbols.length} ad-hoc
              </span>
            </div>
            <label className={styles.sourceField}>
              <span>Add extra symbols (comma or space separated):</span>
              <input
                type="text"
                className={styles.sourceInput}
                placeholder="e.g. AAPL NVDA META"
                value={extraInput}
                onChange={(e) => setExtraInput(e.target.value)}
              />
            </label>
            {combinedSymbols.length === 0 && (
              <p className={styles.sourceHint}>
                No symbols tracked yet — showing the full earnings calendar (capped). Add symbols above, or visit your{' '}
                <Link to="/watchlist">Watchlist</Link> or <Link to="/positions">Positions</Link>.
              </p>
            )}
          </div>

          {/* View toggle */}
          <div className={styles.filterRow}>
            <button
              className={`${styles.filterBtn} ${view === 'list' ? styles.active : ''}`}
              onClick={() => setView('list')}
            >
              List View
            </button>
            <button
              className={`${styles.filterBtn} ${view === 'week' ? styles.active : ''}`}
              onClick={() => setView('week')}
            >
              Weekly View
            </button>
          </div>

          {/* Upcoming earnings */}
          {upcoming.length > 0 ? (
            view === 'list' ? (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Symbol</th>
                      <th>Time</th>
                      <th>Quarter</th>
                      <th>EPS Est.</th>
                      <th>Rev. Est.</th>
                      <th>Countdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming.map((item, i) => {
                      const days = daysUntil(item.date);
                      return (
                        <tr key={`${item.symbol}-${item.date}-${i}`}>
                          <td>{fmtDate(item.date)}</td>
                          <td className={styles.symbol}>{item.symbol}</td>
                          <td>
                            <span className={`${styles.timeBadge} ${styles[item.hour] || ''}`}>
                              {earningsHour(item.hour)}
                            </span>
                          </td>
                          <td>
                            Q{item.quarter} {item.year}
                          </td>
                          <td>{fmtEps(item.eps_estimate)}</td>
                          <td>{fmtRevenue(item.revenue_estimate)}</td>
                          <td>
                            <span className={days <= 1 ? styles.imminent : days <= 7 ? styles.soon : ''}>
                              {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles.weekGroups}>
                {weekGroups.map((group) => (
                  <div key={group.label} className={styles.weekGroup}>
                    <h3 className={styles.weekLabel}>{group.label}</h3>
                    <div className={styles.weekCards}>
                      {group.items.map((item, i) => {
                        const days = daysUntil(item.date);
                        return (
                          <div
                            key={`${item.symbol}-${item.date}-${i}`}
                            className={`${styles.earningsCard} ${days <= 1 ? styles.cardImminent : ''}`}
                          >
                            <div className={styles.cardHeader}>
                              <span className={styles.cardSymbol}>{item.symbol}</span>
                              <span className={`${styles.timeBadge} ${styles[item.hour] || ''}`}>
                                {earningsHour(item.hour)}
                              </span>
                            </div>
                            <div className={styles.cardDate}>{fmtDate(item.date)}</div>
                            <div className={styles.cardDetails}>
                              <span>
                                Q{item.quarter} {item.year}
                              </span>
                              {item.eps_estimate !== null && <span>EPS: {fmtEps(item.eps_estimate)}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <p className={styles.noData}>
              {data.filter_applied
                ? `No upcoming earnings in the next 84 days for the ${combinedSymbols.length} tracked symbol${combinedSymbols.length === 1 ? '' : 's'}. Add more symbols above to widen the search.`
                : 'No upcoming earnings returned by Finnhub for the next 84 days.'}
            </p>
          )}

          {/* Recent earnings (already reported) */}
          {pastRecent.length > 0 && (
            <div className={styles.pastSection}>
              <h2 className={styles.sectionTitle}>Recent Reports</h2>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Symbol</th>
                      <th>EPS Est.</th>
                      <th>EPS Actual</th>
                      <th>Surprise</th>
                      <th>Rev. Est.</th>
                      <th>Rev. Actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastRecent.map((item, i) => {
                      const epsSurprise =
                        item.eps_actual !== null && item.eps_estimate !== null
                          ? item.eps_actual - item.eps_estimate
                          : null;
                      return (
                        <tr key={`past-${item.symbol}-${item.date}-${i}`}>
                          <td>{fmtDate(item.date)}</td>
                          <td className={styles.symbol}>{item.symbol}</td>
                          <td>{fmtEps(item.eps_estimate)}</td>
                          <td className={styles.actual}>{fmtEps(item.eps_actual)}</td>
                          <td>
                            {epsSurprise !== null ? (
                              <span className={epsSurprise >= 0 ? styles.beat : styles.miss}>
                                {epsSurprise >= 0 ? '+' : ''}
                                {epsSurprise.toFixed(2)}
                                {epsSurprise >= 0 ? ' Beat' : ' Miss'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td>{fmtRevenue(item.revenue_estimate)}</td>
                          <td>{fmtRevenue(item.revenue_actual)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className={styles.footer}>
            {data.filter_applied
              ? `Tracking ${combinedSymbols.length} symbol${combinedSymbols.length === 1 ? '' : 's'}`
              : 'Showing all earnings (capped at 200)'}{' '}
            &middot; Data from Finnhub &middot; Updated {new Date(data.fetched_at).toLocaleTimeString()}
          </p>
        </>
      )}

      <Link to="/home" className={styles.back}>
        &larr; Back to Home
      </Link>
    </section>
  );
};

export default Earnings;
